import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import * as echarts from 'echarts';


interface ChartProps {
    option: {
        title: {
            text: string;
            left: string;
        };
        legend: {};
        grid: {};
        yAxis: {};
        xAxis: {};
        series: {}[];
    },
    height?: number
    update?: any
    additionnal_series?: {}[]
}
export default function ChartLoader({ option, height = 200, update, additionnal_series }: ChartProps) {
    const chartRef = useRef(null);
    let chartInstance = useRef(null);
    const title = option.title ? option.title.text : '';
    if (additionnal_series)
        option.series.push(...additionnal_series);

    useEffect(() => {
        updateChart();
    }, [, update]);

    function updateChart() {
        if (chartRef.current) { // TODO test if data is not null

            let element = document.getElementById("graph_" + title);
            if (element) element.style.height = `${height}px`;

            let chartDom: { clientWidth: number, clientHeight: number; innerHTML: any } = chartRef.current;
            if (chartDom.clientWidth === 0 || chartDom.clientHeight === 0) {
                console.error("Chart container has no size. Please ensure it is properly styled with width and height.");
                return;
            }

            if (chartInstance.current) {
                chartInstance.current.dispose();
            }

            chartDom.innerHTML = "";

            chartInstance.current = echarts.init(chartDom);
            chartInstance.current.setOption(option);
        }
    }


    return (<div>
        <div className="h-full justify-center text-center w-full">
            <div id={"graph_" + title} ref={chartRef} className="w-full h-full"></div>
        </div>
    </div>
    );
}


export const ChartGraph = (data: any[], type: string, title: string, option = {}, series = {}) => {
    const { t } = useTranslation();
    return {
        ...option,
        title: {
            text: t(title),
            left: 'center',
        },
        legend: {
            left: '100px',
        },
        grid: {
            left: 50,
            right: 50,
            top: 30,
            bottom: 30
        },
        yAxis: {
            type: 'value',
            axisLabel: { formatter: '{value}' }
        },
        xAxis: {
            type: 'category',
            data: data[1]
        },
        series: [
            {
                data: data[0],
                type: type,
                ...series
            }
        ]
    };
};

export const ChartScatter = (data: any[], title: string) => {
    const { t } = useTranslation();

    return {
        title: {
            text: t(title),
            left: 'center',
        },
        legend: {
            left: '100px',
        },
        grid: {
            left: 50,
            right: 50,
            top: 30,
            bottom: 30
        },
        yAxis: {
            type: 'value',
            axisLabel: { formatter: '{value}' }
        },
        xAxis: {},
        series: [
            {
                data: data,
                type: 'scatter',
            }
        ]
    };
};

export const ChartBarCompare = (
    data: {
        name: string,
        subBar: {
            name: string,
            data: number[]
        }[]
    }[],
    title: string,
    total: boolean = true,
    categories: string[] = ['']
) => {
    const { t } = useTranslation();

    let series: any[] = [];
    data.map(cat => {
        let width = (window.innerWidth / 4) / categories.length;

        if (total) {
            let cat_data = [...Array(categories.length)].map((_, i) => 0)
            cat.subBar.map(sub => sub.data.map((v, i) => cat_data[i] += v))

            series.push({
                name: cat.name,
                type: 'bar',
                barWidth: width / 2,
                emphasis: { focus: 'series' },
                data: cat_data
            })
        }
        cat.subBar.map(sub =>
            series.push({
                name: sub.name,
                type: 'bar',
                barWidth: width,
                emphasis: { focus: 'series' },
                data: sub.data,
                stack: cat.name
            }))
    })


    return {
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' }
        },
        xAxis:
        {
            type: 'category',
            data: categories
        },
        yAxis: {},
        title: {
            text: t(title),
            left: 'right',
        },
        legend: {
            left: '50px',
        },
        grid: {
            left: 50,
            right: 50,
            top: 30,
            bottom: 30
        },
        series: series
    };
};

export const ChartBarLine = (data: any[], title: string) => {
    const { t } = useTranslation();

    return {
        title: {
            text: t(title),
            left: 'center',
        },
        legend: {
            left: '100px',
        },
        grid: {
            left: 50,
            right: 50,
            top: 30,
            bottom: 30
        },
        yAxis: {},
        xAxis: {},
        visualMap: {
            show: false,
            dimension: 0,
            pieces: [
                { lte: 10, color: 'green' },
                { gt: 10, lte: 15, color: 'red' },
                { gt: 15, lte: 27, color: 'green' },
                { gt: 27, lte: 42, color: 'red' },
                { gt: 42, color: 'green' }
            ]
        },
        series: [
            {
                data: data.map((v, i) => [i, v]),
                type: 'bar',
            },
            {
                data: data.map((v, i) => [i, v]),
                type: 'line',
                markArea: {
                    itemStyle: { color: 'rgba(255, 173, 177, 0.4)' },
                    data: [[
                        { name: 'Morning Peak', xAxis: 10 },
                        { xAxis: 15 }
                    ],
                    [
                        { name: 'Evening Peak', xAxis: 27 },
                        { xAxis: 42 }
                    ]]
                }
            }
        ]
    };
};


export const ChartAreaSubstract = (data1: number[], data2: number[], title: string) => {
    const { t } = useTranslation();

    const mergeData = (data1: number[], data2: number[]) => {
        if (!(data1 && data2)) return [[0], [0]];

        if (data1.length !== data2.length) {
            while (data1.length > data2.length)
                data2.push(0);

            while (data2.length > data1.length)
                data1.push(0);
        }

        return [
            ...data1.map((v, i) => [i, v]),
            ...data2.map((v, i) => [data2.length - i - 1, data2[data2.length - i - 1]]) //array reversed (for the polygon)
        ];
    }

    const getRedColumn = (data1: number[], data2: number[]) => {
        if (!(data1 && data2)) return []
        console.log(data1, data2)

        let red_column: [{ name: string, xAxis: number }, { xAxis: number }][] = [];

        let start = -1;
        let count = 0;
        for (let i = 0; i < data1.length; i++) {
            if (data1[i] >= data2[i]) {
                if (start !== -1) {
                    let a = data1[i - 1] - data2[i - 1];
                    let b = data2[i] - data1[i];

                    red_column.push([
                        { name: ((Math.floor(count * 100) / 100) + ''), xAxis: start },
                        { xAxis: (i - 1 + (a / (a + b))) }
                    ]);
                    start = -1;
                    count = 0;
                }
            }
            else if (data1[i] < data2[i]) {
                if (start === -1) {
                    let a = data2[i - 1] - data1[i - 1];
                    let b = data1[i] - data2[i];
                    start = i - 1 + (a / (a + b));
                    if (start <= -1 || start == null || Number.isNaN(start)) start = 0;
                }
                count += (data2[i] - data1[i])
            }
        }
        if (start != -1) red_column.push([
            { name: ((Math.floor(count * 100) / 100) + ''), xAxis: start },
            { xAxis: data1.length - 1 }
        ])
        return red_column;
    }

    const data = mergeData(data1, data2);
    const red_column = getRedColumn(data1, data2)

    return {
        animation: false,
        grid: {
            left: 50,
            right: 50,
            top: 30,
            bottom: 80
        },
        title: {
            text: t(title),
            left: 'center',
        },
        tooltip: { trigger: 'axis' },
        dataZoom: [
            { type: 'slider', filterMode: 'none' },
            { type: 'inside', filterMode: 'none' }
        ],
        xAxis: {
            type: 'value',
            axisLabel: {
                formatter: function (value, index) {
                    return value + 1;
                }
            }
        },
        yAxis: {},
        series: [{
            type: 'custom',
            renderItem: function (params, api) {
                if (params.context.rendered) return;
                params.context.rendered = true;

                let points = data.map(v => api.coord(v));
                let color = api.visual('color');
                return {
                    type: 'polygon',
                    transition: ['shape'],
                    shape: { points: points },
                    style: {
                        fill: '#5c7bd9d0',
                        stroke: color
                    }
                };
            },
            clip: true,
            data: data,
            markArea: {
                itemStyle: { color: 'rgba(255, 173, 177, 0.4)' },
                data: red_column
            }
        },
        {
            data: data1?.map((v, i) => [i, v]),
            type: 'line',
        },
        {
            data: data2?.map((v, i) => [i, v]),
            type: 'line',
        }]
    }
};
